import { useMemo, useState } from "react";
import {
  getCultureSourceReadiness,
  listCultureSourceRuns,
  parseCultureControlsApiError,
  runCultureSource,
  updateCultureSource,
} from "../../../../api/cultureSourceControls";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import AdminBtn from "../layout/AdminBtn";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapCultureSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import { useOverlaySourceSchedule } from "../dataSources/useOverlaySourceSchedule";
import CultureSourceReadinessBadge, {
  resolveCultureReadinessBadges,
} from "./CultureSourceReadinessBadge";
import CultureSourceRunHistory from "./CultureSourceRunHistory";
import CultureSourceRunNowModal from "./CultureSourceRunNowModal";
import { renderCultureHealthAlerts } from "./CultureHealthAlertBadge";

function normalizeHealth(source) {
  if (!source.enabled && !source.isActive) {
    return "disabled";
  }
  if (source.isRunning) {
    return "running";
  }

  const status = String(source.latestRunStatus || source.status || "").toLowerCase();
  if (status === "failed" || status === "error" || status === "parser_failure") {
    return "failed";
  }
  if (status === "success" || status === "active") {
    return "healthy";
  }
  if (status === "discovery_pending" || status === "not_configured") {
    return "never_run";
  }

  return source.healthStatus || "never_run";
}

function resolveRunNowTitle(source) {
  if (source.referenceOnly) {
    return "Reference-only source — use curated seed workflow.";
  }
  if (source.moderationBased) {
    return "Moderation-based source — review submissions in the Culture moderation queue.";
  }
  if (source.blockingReasons?.length) {
    return source.blockingReasons.join("; ");
  }
  if (!source.canRunNow) {
    return source.recommendedNextAction || "This source cannot be run.";
  }
  return "Run culture ingestion now";
}

const CultureSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  setSources,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const [runningCode, setRunningCode] = useState(null);
  const [readinessSource, setReadinessSource] = useState(null);
  const [readinessPayload, setReadinessPayload] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [runsSource, setRunsSource] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");
  const [runNowSource, setRunNowSource] = useState(null);
  const [runNowReadiness, setRunNowReadiness] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const overlaySchedule = useOverlaySourceSchedule({
    domain: "culture",
    onRefresh,
    showToast,
    onScheduleSaved: (source, schedule) => {
      patchSourceInList({
        sourceKey: source.sourceKey,
        scheduleEnabled: schedule.scheduleEnabled,
        scheduleIntervalMinutes: schedule.scheduleIntervalMinutes,
        nextRunAt: schedule.nextRunAt,
        supportsSchedule: true,
      });
    },
  });

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters?.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query)
      );
    }

    if (filters?.enabledFilter === "enabled") {
      list = list.filter((source) => source.enabled || source.isActive);
    } else if (filters?.enabledFilter === "disabled") {
      list = list.filter((source) => !source.enabled && !source.isActive);
    }

    if (filters?.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list
      .sort((a, b) => a.sourceName.localeCompare(b.sourceName))
      .map((source) => mapCultureSourceToGridRow(source));
  }, [sources, filters]);

  if (hideEmpty && !filteredSources.length) {
    return null;
  }

  const patchSourceInList = (updated) => {
    const code = updated?.sourceKey || updated?.sourceCode;
    if (!code) {
      return;
    }

    setSources((current) =>
      current.map((item) =>
        item.sourceKey === code || item.sourceCode === code ? { ...item, ...updated } : item
      )
    );
  };

  const handleToggleActive = async (source) => {
    if (!canManageSources) {
      return;
    }

    const code = source.sourceKey || source.sourceCode;
    const nextEnabled = !(source.enabled ?? source.isActive);
    setBusyCode(code);

    try {
      if (nextEnabled) {
        const readiness = await getCultureSourceReadiness(code);
        if (!readiness.canEnable) {
          showToast(
            readiness.enableBlockingReasons?.[0] ||
              readiness.blockingReasons?.[0] ||
              `${source.sourceName} cannot be enabled.`,
            "error"
          );
          return;
        }
      }

      const updated = await updateCultureSource(code, { enabled: nextEnabled });
      patchSourceInList(updated);
      showToast(
        nextEnabled ? `${source.sourceName} enabled.` : `${source.sourceName} disabled.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      const parsed = parseCultureControlsApiError(error);
      showToast(parsed.message, "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleRunNow = async (source, runOptions = { dryRun: false }) => {
    const code = source.sourceKey || source.sourceCode;
    setBusyCode(code);
    setRunningCode(code);
    setRunNowSource(null);

    setSources((current) =>
      current.map((item) =>
        item.sourceKey === code || item.sourceCode === code
          ? { ...item, isRunning: true }
          : item
      )
    );

    try {
      const result = await runCultureSource(code, runOptions);
      const modeLabel = runOptions.dryRun ? "Dry-run" : "Run";
      showToast(
        `${modeLabel} ${source.sourceName}: found ${result.recordsFound ?? 0}, inserted ${result.recordsInserted ?? 0}, updated ${result.recordsUpdated ?? 0}.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      const parsed = parseCultureControlsApiError(error);
      showToast(parsed.message, "error");
      onRefresh?.({ silent: true });
    } finally {
      setBusyCode(null);
      setRunningCode(null);
      setSources((current) =>
        current.map((item) =>
          item.sourceKey === code || item.sourceCode === code
            ? { ...item, isRunning: false }
            : item
        )
      );
    }
  };

  const handleOpenRunNow = async (source) => {
    const code = source.sourceKey || source.sourceCode;
    if (code === "BIBLIOTECAS_MEDELLIN_EVENTS") {
      setRunNowSource(source);
      setRunNowReadiness(null);
      try {
        const payload = await getCultureSourceReadiness(code);
        setRunNowReadiness(payload);
      } catch (error) {
        const parsed = parseCultureControlsApiError(error);
        setRunNowReadiness({ blockingReasons: parsed.blockingReasons, error: parsed.message });
      }
      return;
    }

    await handleRunNow(source, { dryRun: false });
  };

  const handleViewRuns = async (source) => {
    const code = source.sourceKey || source.sourceCode;
    setRunsSource(source);
    setRuns([]);
    setRunsError("");
    setRunsLoading(true);

    try {
      const payload = await listCultureSourceRuns(code, { limit: 25 });
      setRuns(payload.records || []);
    } catch (error) {
      setRunsError(error.message || "Unable to load run history.");
    } finally {
      setRunsLoading(false);
    }
  };

  const handleRefreshRuns = async () => {
    if (!runsSource) {
      return;
    }
    await handleViewRuns(runsSource);
  };

  const handleViewReadiness = async (source) => {
    const code = source.sourceKey || source.sourceCode;
    setReadinessSource(source);
    setReadinessPayload(null);
    setReadinessLoading(true);

    try {
      const payload = await getCultureSourceReadiness(code);
      setReadinessPayload(payload);
    } catch (error) {
      const parsed = parseCultureControlsApiError(error);
      setReadinessPayload({ error: parsed.message, blockingReasons: parsed.blockingReasons });
    } finally {
      setReadinessLoading(false);
    }
  };

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No culture sources match the current filters."
        busyKey={busyCode || runningCode}
        canManageSources={canManageSources}
        onToggleEnabled={handleToggleActive}
        onRunNow={handleOpenRunNow}
        onSaveSchedule={overlaySchedule.handleSaveSchedule}
        onViewRuns={handleViewRuns}
        sectionLabel="Culture"
        deleteDomain="culture"
        onDeleted={() => onRefresh?.()}
        resolveRunNowTitle={resolveRunNowTitle}
        extraActions={(source, { isBusy }) => (
          <AdminBtn variant="ghost" disabled={isBusy} onClick={() => handleViewReadiness(source)}>
            Details
          </AdminBtn>
        )}
        renderSourceExtras={(source) => {
          const badges = resolveCultureReadinessBadges(source);
          return (
            <div className="culture-readiness-badge-row" aria-label="Source signals">
              {badges.map((badge) => (
                <CultureSourceReadinessBadge
                  key={badge.key}
                  label={badge.label}
                  kind={badge.kind}
                  title={badge.title}
                />
              ))}
              {renderCultureHealthAlerts(source.healthAlerts, 8)}
            </div>
          );
        }}
      />
      <DataSourceScheduleModal
        source={overlaySchedule.scheduleSource}
        isOpen={Boolean(overlaySchedule.scheduleSource)}
        isSaving={overlaySchedule.scheduleSaving}
        onClose={() => overlaySchedule.setScheduleSource(null)}
        onSave={overlaySchedule.handleSaveSchedule}
      />

      <CultureSourceRunHistory
        source={runsSource}
        runs={runs}
        isOpen={Boolean(runsSource)}
        isLoading={runsLoading}
        errorMessage={runsError}
        onClose={() => setRunsSource(null)}
        onRefresh={handleRefreshRuns}
      />

      <CultureSourceRunNowModal
        source={runNowSource}
        readinessPayload={runNowReadiness}
        isOpen={Boolean(runNowSource)}
        isRunning={Boolean(runNowSource && runningCode === runNowSource.sourceKey)}
        onClose={() => {
          setRunNowSource(null);
          setRunNowReadiness(null);
        }}
        onConfirm={(runOptions) => handleRunNow(runNowSource, runOptions)}
      />

      {readinessSource ? (
        <div className="admin-re-modal-backdrop" role="presentation" onClick={() => setReadinessSource(null)}>
          <div
            className="admin-re-modal panel-card"
            role="dialog"
            aria-labelledby="culture-readiness-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="admin-re-modal-header">
              <div>
                <p className="eyebrow">Source readiness</p>
                <h2 id="culture-readiness-modal-title">{readinessSource.sourceName}</h2>
              </div>
              <button type="button" className="midpoint-share-button" onClick={() => setReadinessSource(null)}>
                Close
              </button>
            </header>
            <div className="admin-re-modal-body">
              {readinessLoading ? <p className="muted">Loading readiness…</p> : null}
              {readinessPayload?.error ? (
                <p className="admin-data-health-error">{readinessPayload.error}</p>
              ) : null}
              {readinessPayload && !readinessLoading ? (
                <ul className="culture-readiness-detail-list">
                  <li>Tier: {readinessPayload.readinessTier}</li>
                  <li>Discovery status: {readinessPayload.discoveryStatus || "—"}</li>
                  <li>Can run now: {readinessPayload.canRunNow ? "Yes" : "No"}</li>
                  <li>
                    Real ingest: {readinessPayload.canApplyRealIngest ? "Available" : "Blocked"}
                  </li>
                  <li>Runner: {readinessPayload.runnerType || "—"}</li>
                  <li>Current run state: {readinessPayload.currentRunState || "—"}</li>
                  {readinessPayload.feedUrl ? <li>Feed URL: {readinessPayload.feedUrl}</li> : null}
                  {readinessPayload.calendarUrl ? (
                    <li>Calendar URL: {readinessPayload.calendarUrl}</li>
                  ) : null}
                  {readinessPayload.attribution ? (
                    <li>Attribution: {readinessPayload.attribution}</li>
                  ) : null}
                  {readinessPayload.envVarName ? (
                    <li>
                      Env var: {readinessPayload.envVarName} (
                      {readinessPayload.envVarEnabled ? "enabled" : "disabled"})
                    </li>
                  ) : null}
                  {readinessPayload.documentationUrl ? (
                    <li>Docs: {readinessPayload.documentationUrl}</li>
                  ) : null}
                  {readinessPayload.adminMetrics ? (
                    <>
                      <li>Upcoming events: {readinessPayload.adminMetrics.upcomingEventCount ?? "—"}</li>
                      <li>Total events: {readinessPayload.adminMetrics.totalEventCount ?? "—"}</li>
                      <li>
                        Venue match rate:{" "}
                        {readinessPayload.adminMetrics.venueMatchRate != null
                          ? `${Math.round(readinessPayload.adminMetrics.venueMatchRate * 100)}%`
                          : "—"}
                      </li>
                      <li>
                        Tag coverage:{" "}
                        {readinessPayload.adminMetrics.tagCoverageRate != null
                          ? `${Math.round(readinessPayload.adminMetrics.tagCoverageRate * 100)}%`
                          : "—"}
                      </li>
                      <li>
                        Parser drift: {readinessPayload.adminMetrics.parserDriftStatus || "—"}
                      </li>
                    </>
                  ) : null}
                  {readinessPayload.recommendedNextAction ? (
                    <li>Next action: {readinessPayload.recommendedNextAction}</li>
                  ) : null}
                  {readinessPayload.blockingReasons?.length ? (
                    <li>Blockers: {readinessPayload.blockingReasons.join("; ")}</li>
                  ) : null}
                  {readinessPayload.warnings?.length ? (
                    <li>Warnings: {readinessPayload.warnings.join("; ")}</li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CultureSourceControlsTable;
